import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Select,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  IconButton,
  useToast,
  Divider,
  Flex,
  Tooltip,
} from "@chakra-ui/react";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import { BASE_URLS } from "./env";

const EVENT_TYPE_LABELS = {
  session_start: "Signed in",
  session_end: "Signed out",
  page_visit: "Page visit",
  ai_query: "AI query",
  audio_record: "Audio recorded",
  transcript_complete: "Transcript completed",
  document_edit: "Document edited",
  form_submit: "Form submitted",
  schema_switch: "Schema switched",
};

const EVENT_TYPE_COLORS = {
  session_start: "green",
  session_end: "gray",
  page_visit: "blue",
  ai_query: "cyan",
  audio_record: "purple",
  transcript_complete: "pink",
  document_edit: "orange",
  form_submit: "teal",
  schema_switch: "yellow",
};

const EVENT_TYPE_OPTIONS = [
  { value: "all", label: "All event types" },
  ...Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

function backendUrl() {
  const override = process.env.REACT_APP_BACKEND_URL;
  if (override && override.trim() !== "") return override;
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1"
    ? BASE_URLS.local.backend
    : BASE_URLS.render.backend;
}

async function authHeaders() {
  const mod = await import("./supabaseClient");
  const { data } = await mod.supabasePublic().auth.getSession();
  const token = data?.session?.access_token;
  return {
    Authorization: token ? `Bearer ${token}` : "",
    "X-Schema": mod.supabaseSchema || "public",
  };
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatDurationMs(ms) {
  if (ms == null) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

function describeEvent(ev) {
  const meta = ev.metadata || {};
  if (ev.event_type === "ai_query") {
    return meta.feature ? `feature: ${meta.feature}` : null;
  }
  if (ev.event_type === "schema_switch") {
    if (meta.from && meta.to) return `${meta.from} → ${meta.to}`;
    return null;
  }
  if (ev.event_type === "session_end" && meta.reason) {
    return `reason: ${meta.reason}`;
  }
  return null;
}

const HistoryModal = ({ isOpen, onClose }) => {
  const [events, setEvents] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [limit, setLimit] = useState(100);
  const [scope, setScope] = useState("my"); // 'my' | 'all'
  const [adminAllowed, setAdminAllowed] = useState(false);
  const toast = useToast();

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (eventTypeFilter !== "all") params.set("event_type", eventTypeFilter);
      if (scope === "all") params.set("scope", "all");
      const res = await fetch(
        `${backendUrl()}/api/analytics/user-events/?${params.toString()}`,
        { headers },
      );
      if (res.status === 403) {
        setAdminAllowed(false);
        setScope("my");
        throw new Error("Admin access required");
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setEvents(Array.isArray(json.events) ? json.events : []);
      setUserInfo(json.user || null);
      if (scope === "all") setAdminAllowed(true);
    } catch (e) {
      setError(e.message || "Failed to load");
      toast({
        title: "Couldn't load activity",
        description: e.message || "Unknown error",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [limit, eventTypeFilter, scope, toast]);

  useEffect(() => {
    if (isOpen) fetchEvents();
  }, [isOpen, fetchEvents]);

  // Probe admin once — a 403 on scope=all tells us to hide the toggle.
  useEffect(() => {
    if (!isOpen || adminAllowed) return;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(
          `${backendUrl()}/api/analytics/user-events/?scope=all&limit=1`,
          { headers },
        );
        if (res.ok) setAdminAllowed(true);
      } catch (_) {}
    })();
  }, [isOpen, adminAllowed]);

  const filtered = useMemo(() => {
    if (!searchQuery) return events;
    const q = searchQuery.toLowerCase();
    return events.filter((ev) => {
      if ((ev.event_type || "").toLowerCase().includes(q)) return true;
      if ((ev.page || "").toLowerCase().includes(q)) return true;
      if ((ev._schema || "").toLowerCase().includes(q)) return true;
      try {
        return JSON.stringify(ev.metadata || {}).toLowerCase().includes(q);
      } catch (_) {
        return false;
      }
    });
  }, [events, searchQuery]);

  const handleExport = () => {
    try {
      const blob = new Blob([JSON.stringify(events, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: "Exported", status: "success", duration: 2000 });
    } catch (_) {
      toast({ title: "Export failed", status: "error", duration: 3000 });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack justify="space-between">
            <Text>Activity History</Text>
            {userInfo && (
              <Badge colorScheme="purple" fontSize="sm">
                {userInfo.name || userInfo.email || userInfo.id}
              </Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <Box mb={4}>
            <HStack spacing={3} mb={3} flexWrap="wrap">
              <InputGroup maxW="280px">
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search events, pages, metadata…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Select
                maxW="220px"
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
              >
                {EVENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>

              <Select
                maxW="160px"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                <option value="50">Last 50</option>
                <option value="100">Last 100</option>
                <option value="250">Last 250</option>
                <option value="500">Last 500</option>
                <option value="1000">Last 1000</option>
              </Select>

              {adminAllowed && (
                <Select
                  maxW="200px"
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                >
                  <option value="my">My activity</option>
                  <option value="all">All tenants (admin)</option>
                </Select>
              )}

              <Tooltip label="Export as JSON">
                <IconButton
                  icon={<DownloadIcon />}
                  size="sm"
                  onClick={handleExport}
                  aria-label="Export history"
                  isDisabled={events.length === 0}
                />
              </Tooltip>
            </HStack>
          </Box>

          <Divider mb={4} />

          {loading ? (
            <Flex justify="center" align="center" minH="200px">
              <VStack spacing={3}>
                <Spinner size="lg" color="blue.500" />
                <Text color="gray.500">Loading activity…</Text>
              </VStack>
            </Flex>
          ) : error ? (
            <Flex justify="center" align="center" minH="200px">
              <VStack spacing={3}>
                <Text color="red.500">{error}</Text>
                <Button size="sm" onClick={fetchEvents}>
                  Retry
                </Button>
              </VStack>
            </Flex>
          ) : filtered.length === 0 ? (
            <Flex justify="center" align="center" minH="200px">
              <Text color="gray.500">
                No events found{searchQuery && " matching your search"}.
              </Text>
            </Flex>
          ) : (
            <VStack spacing={0} align="stretch" maxH="480px" overflowY="auto">
              {filtered.map((ev, idx) => {
                const label =
                  EVENT_TYPE_LABELS[ev.event_type] || ev.event_type;
                const color = EVENT_TYPE_COLORS[ev.event_type] || "gray";
                const desc = describeEvent(ev);
                const dur = formatDurationMs(ev.duration_ms);
                return (
                  <Box
                    key={ev.id || idx}
                    p={3}
                    borderBottom="1px"
                    borderColor="gray.100"
                    _hover={{ bg: "gray.50" }}
                  >
                    <HStack justify="space-between" align="start">
                      <Box flex="1" minW={0}>
                        <HStack spacing={2} mb={1} flexWrap="wrap">
                          <Badge colorScheme={color}>{label}</Badge>
                          {ev.page && (
                            <Text fontSize="sm" color="gray.600" isTruncated>
                              {ev.page}
                            </Text>
                          )}
                          {ev._schema && (
                            <Badge
                              colorScheme="purple"
                              variant="outline"
                              fontSize="xs"
                            >
                              {ev._schema}
                            </Badge>
                          )}
                          {dur && (
                            <Badge colorScheme="gray" variant="subtle">
                              {dur}
                            </Badge>
                          )}
                        </HStack>
                        {desc && (
                          <Text fontSize="sm" color="gray.500">
                            {desc}
                          </Text>
                        )}
                      </Box>
                      <VStack spacing={0} align="end" minW="120px">
                        <Text fontSize="sm">
                          {formatRelative(ev.created_at)}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          {new Date(ev.created_at).toLocaleString()}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}

          {filtered.length > 0 && (
            <Box mt={4} pt={4} borderTop="1px" borderColor="gray.200">
              <HStack justify="space-between">
                <Text fontSize="sm" color="gray.500">
                  Showing {filtered.length} of {events.length} events
                </Text>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLimit((n) => Math.min(n + 250, 2000))}
                  isDisabled={events.length < limit}
                >
                  Load more
                </Button>
              </HStack>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default HistoryModal;
